
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useUserStore } from "@/store/userStore";
import { useAppName } from "@/hooks/useAppName";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
//  specialization: z.string().min(1, "Specialization is required"),
  experience: z.string().optional(),
 // bio: z.string().optional(),
 user_id:z.string().optional()
});

type FormData = z.infer<typeof formSchema>;

interface AddCoachDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCoachAdded: () => void;
}

export function AddCoachDialog({ open, onOpenChange, onCoachAdded }: AddCoachDialogProps) {
  const { t } = useTranslation(["coaches", "common", "coach"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const gymId = useUserStore((state) => state.gymId);
  const appInfo = useAppName();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      user_id:"",
      //specialization: "",
      experience: "",
     // bio: "",
    },
  });

  // Brouillon persisté : la saisie survit à un reload (règle : Annuler
  // explicite efface, Escape/overlay conserve).
  const { clearDraft } = useFormDraft("add-coach", form, {
    exclude: ["user_id"],
    isEmpty: (v) => !v.firstName && !v.lastName && !v.email,
  });

  const onSubmit = async (values: FormData) => {
    setIsSubmitting(true);
    try {
      // Call Edge Function to create coach user
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          gym_id: gymId,
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phone || '',
          role: 'coach',
          specialization: '',
          experience: values.experience || '',
          app_name: appInfo
        }
      });

      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create coach');
      }

      toast.success(t("coachAddedSuccess", { ns: "coach" }));
      clearDraft();
      form.reset();
      onOpenChange(false);
      onCoachAdded();
      
    } catch (error) {
      toast.error(t("errorOccurred", { ns: "common" }));
      console.error("Error creating coach:", error);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("addCoach", { ns: "coaches" })}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("firstName", { ns: "coaches" })}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("lastName", { ns: "coaches" })}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email", { ns: "coaches" })}</FormLabel>
                  <FormControl>
                    <Input type="email" required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("phone", { ns: "coaches" })}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="experience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("experience", { ns: "coaches" })}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("selectExperience", { ns: "coach" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1-2">{t("experienceYears.beginner", { ns: "coaches" })}</SelectItem>
                      <SelectItem value="3-5">{t("experienceYears.intermediate", { ns: "coaches" })}</SelectItem>
                      <SelectItem value="5-10">{t("experienceYears.advanced", { ns: "coaches" })}</SelectItem>
                      <SelectItem value="10+">{t("experienceYears.expert", { ns: "coaches" })}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("bio", { ns: "coach" })}</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-32" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <DialogFooter>
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button variant="outline" type="button" onClick={() => { clearDraft(); onOpenChange(false); }}>
                {t("cancel", { ns: "common" })}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("saving", { ns: "common" }) : t("save", { ns: "common" })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
